/**
 * Disclosures M4 — small shared building blocks reused across the seven
 * editor screens. Kept private to the Disclosures module (underscore prefix
 * signals "internal export"); not promoted to the panel-wide component
 * library until a third surface needs them.
 *
 * Pieces:
 *   - <Toast>     — top-right transient ack/error chip, auto-dismiss
 *   - useToast()  — `{toast, push}` hook around <Toast>
 *   - <Modal>     — simple backdrop + card; relies on existing `.modal*` CSS
 *   - <Field>     — labeled input/textarea/select wrapper
 *   - <PillStatus>— status enum -> styled pill
 *   - useActor()  — reads the MSAL upn from AuthContext for state-machine
 *                   transition POSTs (`actor` body field)
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { CheckCircle2, X, AlertCircle } from '@retail-ops/panel-shell';

import { useAuth } from '@/contexts/AuthContext';

import { statusLabel } from './disclosureModel';

// ─────────────────────────────────────────────────────────────────────────
// Toast.
// ─────────────────────────────────────────────────────────────────────────

export type ToastKind = 'ok' | 'err';

export interface ToastState {
  kind: ToastKind;
  message: string;
  id: number;
}

export function useToast(): {
  toast: ToastState | null;
  push: (kind: ToastKind, message: string) => void;
  clear: () => void;
} {
  const [toast, setToast] = useState<ToastState | null>(null);

  const push = useCallback((kind: ToastKind, message: string) => {
    setToast({ kind, message, id: Date.now() });
  }, []);

  const clear = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), toast.kind === 'ok' ? 3500 : 6000);
    return () => window.clearTimeout(t);
  }, [toast]);

  return { toast, push, clear };
}

export function Toast({ toast, onClose }: { toast: ToastState | null; onClose: () => void }): JSX.Element | null {
  if (!toast) return null;
  const isOk = toast.kind === 'ok';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 1100,
        background: isOk ? 'var(--ok-bg)' : 'var(--err-bg)',
        color: isOk ? 'var(--ok-text)' : 'var(--err-text)',
        padding: '10px 14px',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        boxShadow: '0 6px 20px rgba(15,22,40,0.18)',
        maxWidth: 460,
      }}
    >
      {isOk ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss notification"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          padding: 2,
          display: 'inline-flex',
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Modal — backdrop + card. Wider variant for the version body editor.
// ─────────────────────────────────────────────────────────────────────────

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  closeOnBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 560,
  closeOnBackdrop = true,
}: ModalProps): JSX.Element | null {
  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (closeOnBackdrop) onClose();
      }}
    >
      <div
        className="modal"
        style={{ maxWidth: width, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="btn btn--ghost btn--sm"
            style={{ padding: 4 }}
          >
            <X size={14} />
          </button>
        </header>
        <div>{children}</div>
        {footer && <div className="modal__actions">{footer}</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Field wrappers.
// ─────────────────────────────────────────────────────────────────────────

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  span?: 1 | 2 | 3;
}

export function Field({ label, hint, required, children, span = 1 }: FieldProps): JSX.Element {
  return (
    <label
      className="form-field"
      style={{
        gridColumn: span > 1 ? `span ${span}` : undefined,
        display: 'block',
      }}
    >
      <span className="form-field__label">
        {label}
        {required && <span aria-hidden="true" style={{ color: 'var(--pcb-red)' }}> *</span>}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: 11, color: 'var(--ink-500)', display: 'block', marginTop: 4 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function FieldGrid({ children, columns = 2 }: { children: ReactNode; columns?: number }): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '0 16px',
      }}
    >
      {children}
    </div>
  );
}

const baseInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-300)',
  borderRadius: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  background: 'var(--surface-card)',
};

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  const { style, ...rest } = props;
  return <input type="text" {...rest} style={{ ...baseInputStyle, ...style }} />;
}

export function NumberInput(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  const { style, ...rest } = props;
  return <input type="number" {...rest} style={{ ...baseInputStyle, ...style }} />;
}

export function DateInput(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  const { style, ...rest } = props;
  return <input type="date" {...rest} style={{ ...baseInputStyle, ...style }} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  const { style, ...rest } = props;
  return <textarea {...rest} style={{ ...baseInputStyle, fontFamily: 'var(--font-sans)', resize: 'vertical', ...style }} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  const { style, ...rest } = props;
  return <select {...rest} style={{ ...baseInputStyle, ...style }} />;
}

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }): JSX.Element {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      <input type="checkbox" {...rest} /> {label}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Status pill — maps DisclosureTemplateVersion.Status → pill class.
// ─────────────────────────────────────────────────────────────────────────

export function PillStatus({ status }: { status?: string | null }): JSX.Element {
  const v = String(status ?? '').trim();
  const cls = useMemo(() => {
    switch (v) {
      case 'Active':
        return 'pill pill--ok';
      case 'Approved':
        return 'pill pill--info';
      case 'InReview':
        return 'pill pill--warn';
      case 'Draft':
        return 'pill pill--muted';
      case 'Retired':
        return 'pill pill--err';
      default:
        return 'pill pill--muted';
    }
  }, [v]);
  return (
    <span className={cls} title={v ? `Status: ${v}` : undefined}>
      {v ? statusLabel(v) : '—'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pickers — closed value sets only (BACKLOG-1755: no free-text codes).
// ─────────────────────────────────────────────────────────────────────────

export interface PickItem {
  value: string;
  label: string;
  meaning?: string;
}

/**
 * Select over a closed list. A current value that is not in the list (a legacy
 * value already on the row) is kept as its own option, marked, so opening and
 * saving the form never silently rewrites it.
 */
export function VocabSelect({
  items,
  value,
  onChange,
  ariaLabel,
  allowEmpty,
  emptyLabel = '—',
}: {
  items: ReadonlyArray<PickItem>;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}): JSX.Element {
  const known = items.some((i) => i.value === value);
  const current = items.find((i) => i.value === value);
  return (
    <>
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} title={ariaLabel}>
        {allowEmpty && <option value="">{emptyLabel}</option>}
        {!known && value !== '' && <option value={value}>{value} (not in the list the code knows)</option>}
        {items.map((i) => (
          <option key={i.value} value={i.value}>
            {i.label}
          </option>
        ))}
      </Select>
      {current?.meaning && (
        <span style={{ fontSize: 11, color: 'var(--ink-500)', display: 'block', marginTop: 4 }}>
          {current.meaning}
        </span>
      )}
    </>
  );
}

/**
 * Multi-select as toggle chips. `values` empty means "all" when `allLabel` is
 * given (rendered as the first chip). Unknown current values stay selected and
 * are marked so they can be removed deliberately.
 */
export function ChipPicker({
  items,
  values,
  onChange,
  allLabel,
  ariaLabel,
  disabled,
}: {
  items: ReadonlyArray<PickItem>;
  values: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  allLabel?: string;
  ariaLabel: string;
  disabled?: boolean;
}): JSX.Element {
  const lower = new Set(values.map((v) => v.toLowerCase()));
  const unknown = values.filter((v) => !items.some((i) => i.value.toLowerCase() === v.toLowerCase()));
  const toggle = (v: string): void => {
    if (disabled) return;
    if (lower.has(v.toLowerCase())) onChange(values.filter((x) => x.toLowerCase() !== v.toLowerCase()));
    else onChange([...values, v]);
  };
  const chip = (key: string, label: string, on: boolean, onClick: () => void, title?: string, warn?: boolean) => (
    <button
      key={key}
      type="button"
      role="checkbox"
      aria-checked={on}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`pill ${on ? (warn ? 'pill--warn' : 'pill--info') : 'pill--muted'}`}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: on ? '1px solid var(--pcb-blue)' : '1px solid var(--ink-300)',
        fontSize: 12,
        padding: '4px 10px',
      }}
    >
      {on ? '✓ ' : ''}
      {label}
    </button>
  );
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {allLabel !== undefined && chip('_all', allLabel, values.length === 0, () => !disabled && onChange([]))}
      {items.map((i) => chip(i.value, i.label, lower.has(i.value.toLowerCase()), () => toggle(i.value), i.meaning ?? i.value))}
      {unknown.map((u) => chip(`_u_${u}`, `${u} (not in the catalog)`, true, () => toggle(u), 'Not in the catalog the code reads — click to remove', true))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// useActor — short helper. Returns the MSAL upn for actor field on
// state-machine transitions; falls back to a sentinel string so the server
// audit row never has a NULL actor.
// ─────────────────────────────────────────────────────────────────────────

export function useActor(): string {
  const { user } = useAuth();
  return user?.upn ?? 'unknown@mypcb.com';
}

// ─────────────────────────────────────────────────────────────────────────
// Confirm dialog inline shim — small "are you sure" prompt over Modal.
// ─────────────────────────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  confirmKind = 'danger',
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  confirmKind?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      width={440}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${confirmKind === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{message}</div>
    </Modal>
  );
}
